import type { ReactNode } from "react";
import { Check } from "lucide-react";
import iconImg from "@/assets/icon.png";
import { useBranding } from "@/hooks/useBranding";
import { cn } from "@/lib/utils";

interface OnboardingQuestionProps {
  eyebrow: string;
  title: string;
  description?: string;
  coachCopy: string;
  coachBadge?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Shared Duolingo-style question frame used by every onboarding step. */
export function OnboardingQuestion({
  eyebrow,
  title,
  description,
  coachCopy,
  coachBadge = "★",
  children,
  className,
}: OnboardingQuestionProps) {
  const { branding } = useBranding();
  const coachIcon = branding?.chatIdleIconUrl || iconImg;

  return (
    <div
      className={cn(
        "grid items-start gap-5 lg:grid-cols-[150px_minmax(0,700px)] lg:gap-9",
        className
      )}
    >
      <aside className="relative flex items-center gap-3 lg:block lg:w-[132px] lg:pt-1">
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border-[3px] border-white bg-forest shadow-[0_14px_35px_rgba(25,58,43,0.14)] lg:mx-auto lg:h-[92px] lg:w-[92px] lg:border-4">
          <img src={coachIcon} alt="golfrecruit.ai coach" className="h-full w-full object-cover" />
        </div>
        <span className="absolute left-10 top-9 grid h-6 min-w-6 place-items-center rounded-full border-2 border-surface bg-amber px-1 text-[11px] font-extrabold text-forest lg:left-auto lg:right-0 lg:top-[74px] lg:h-8 lg:min-w-8 lg:border-[3px] lg:text-xs">
          {coachBadge}
        </span>
        <p className="m-0 flex-1 rounded-[9px_18px_18px_18px] border border-border bg-white px-3 py-2.5 text-xs font-semibold leading-relaxed text-forest-soft shadow-[0_6px_20px_rgba(25,58,43,0.05)] lg:mt-2 lg:px-4 lg:py-3 lg:text-sm">
          {coachCopy}
        </p>
      </aside>

      <section className="min-w-0">
        <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-primary-text">
          {eyebrow}
        </p>
        <h2 className="max-w-[700px] text-[clamp(2.25rem,4.6vw,3.4rem)] leading-[1.04] tracking-[-0.045em] text-forest">
          {title}
        </h2>
        {description && (
          <p className="mb-7 mt-3 max-w-[620px] text-sm leading-relaxed text-muted-foreground sm:text-base">
            {description}
          </p>
        )}
        {children}
      </section>
    </div>
  );
}

interface ChoiceCardProps {
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  title: string;
  description?: string;
  multiple?: boolean;
  className?: string;
  testId?: string;
  disabled?: boolean;
}

/** Large, plain-language answer target for single- and multi-select questions. */
export function ChoiceCard({
  selected,
  onClick,
  icon,
  title,
  description,
  multiple = false,
  className,
  testId,
  disabled = false,
}: ChoiceCardProps) {
  return (
    <button
      type="button"
      aria-label={title}
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        "group relative flex min-h-[82px] w-full items-center gap-4 rounded-2xl border-2 bg-white px-4 py-3.5 text-left shadow-[0_4px_0_#cbd8cd] transition duration-150",
        "hover:-translate-y-0.5 hover:border-border-strong hover:shadow-[0_6px_0_#cbd8cd]",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber/70",
        selected &&
          "border-primary-text bg-lime-soft shadow-[0_4px_0_#377e24] hover:border-primary-text hover:shadow-[0_6px_0_#377e24]",
        disabled && "cursor-not-allowed opacity-50 hover:translate-y-0",
        className
      )}
    >
      <span
        className={cn(
          "grid h-12 min-w-12 shrink-0 place-items-center rounded-[14px] bg-surface px-2 font-mono text-sm font-bold text-forest",
          selected && "bg-lime-soft"
        )}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 pr-8">
        <span className="block text-sm font-extrabold leading-tight text-forest sm:text-[15px]">
          {title}
        </span>
        {description && (
          <span className="mt-1 block text-xs leading-snug text-muted-foreground">
            {description}
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        className={cn(
          "absolute right-4 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border-2 border-border-strong bg-white",
          selected && "border-forest bg-forest",
          selected && !multiple && "border-[6px] bg-white"
        )}
      >
        {selected && multiple && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
      </span>
    </button>
  );
}

interface SelectionSectionProps {
  label: string;
  help?: string;
  children: ReactNode;
}

export function SelectionSection({ label, help, children }: SelectionSectionProps) {
  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-sm font-extrabold text-forest">{label}</h3>
        {help && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{help}</p>}
      </div>
      {children}
    </section>
  );
}
